/**
 * Workspace store — the single source of truth that every mounted widget
 * (buddy, badges, streak, marketplace, evolution ceremony) reads from.
 *
 * Design notes:
 *   - Plain pub/sub, no framework coupling. Each Shadow DOM bundle has its
 *     own Preact module graph, so depending on `@preact/signals` here would
 *     break reactivity across bundles. A class with getState/subscribe works
 *     regardless of which bundle imports it.
 *   - The *live* instance lives on `window.__HATCHED_WIDGET__.store`, created
 *     by the loader. Widgets reach it via `getStore()` so they share state
 *     with every other mount on the page, including the loader's smart-poll.
 *   - `applyEffects` is the server-authoritative merge path (reconcile after
 *     a track() round-trip or a /widget/state refresh). `applyOptimistic`
 *     lets a widget paint the predicted result before the server answers;
 *     if the request fails we `rollback` to the pre-optimistic snapshot.
 */
import type {
  BuddyData,
  BadgeCatalogResponse,
  EarnedBadge,
  WidgetAttribution,
} from './types';

export type PathSubStepStatus = 'locked' | 'available' | 'completed';
export type PathIcon = 'path' | 'flame' | 'heart' | 'bolt' | 'star' | 'leaf';

export interface PathSubStepView {
  id: string;
  key: string;
  label: string;
  description: string | null;
  ordinal: number;
  reward_coins: number;
  reward_badge_key: string | null;
  content_url: string | null;
  cta_label: string | null;
  allow_manual_complete: boolean;
  allow_skip_ahead: boolean;
  is_active: boolean;
  status: PathSubStepStatus;
  completed_at: string | null;
}

export interface PathStepView {
  id: string;
  key: string;
  label: string;
  description: string | null;
  icon: string | null;
  ordinal: number;
  reward_coins: number;
  reward_badge_key: string | null;
  is_active: boolean;
  unlocked: boolean;
  completed: boolean;
  sub_steps: PathSubStepView[];
}

export interface PathPayload {
  definition: {
    key: string;
    label: string;
    description: string | null;
    icon: PathIcon;
    accent_color: string | null;
    display_mode: 'straight' | 'zigzag' | 'stepper';
  };
  steps: PathStepView[];
  current_step_key: string | null;
  completed: boolean;
  completed_at: string | null;
}

export interface PathSubStepCompletedEffect {
  path_key: string;
  step_key: string;
  sub_step_key: string;
  reward_coins: number;
  reward_badge_key: string | null;
}

export interface PathStepCompletedEffect {
  path_key: string;
  step_key: string;
  reward_coins: number;
  reward_badge_key: string | null;
}

export interface PathCompletedEffect {
  path_key: string;
}

export interface PathUpdate {
  path_key: string;
  sub_step_completed?: PathSubStepCompletedEffect;
  step_completed?: PathStepCompletedEffect;
  path_completed?: PathCompletedEffect;
}

export interface StreakPayload {
  definition: {
    key: string;
    label: string;
    description: string | null;
    period: 'daily' | 'weekly' | 'monthly';
    icon: string;
    display_mode: 'count' | 'row' | 'mini';
    max_row_icons: number;
    milestones: number[];
  };
  progress: {
    current: number;
    longest: number;
    last_event_at: string | null;
    last_period_key: string | null;
    next_milestone: number | null;
  };
  is_active_period: boolean;
}

export interface EventStreakUpdate {
  definition_key: string;
  label: string;
  icon: string;
  current: number;
  longest: number;
  milestone_hit: number | null;
  hero: boolean;
}

export interface TokenBalance {
  token_key: string;
  label: string;
  icon: string | null;
  balance: number;
  /** Cap for progression tokens (gate currencies) — null when uncapped. */
  max_balance: number | null;
}

export interface TokensPayload {
  /** Spendable primary currency (coins, possibly renamed/re-iconed). */
  primary: { token_key: string; label: string; icon: string | null; balance: number };
  /** Non-spendable progression tokens — the gate currencies. */
  progression: TokenBalance[];
}

export interface EventEffects {
  coins: number;
  badges_awarded: string[];
  badges_ready: string[];
  tokens: string[];
  evolution_ready: boolean;
  streak_milestones?: number[];
  streak_updates?: EventStreakUpdate[];
  path_updates?: PathUpdate[];
}

export interface PendingOp {
  id: string;
  type: 'hatch' | 'evolve' | 'equip_item';
  status: 'pending' | 'processing';
  startedAt: number;
}

export interface WorkspaceState {
  buddy: BuddyData | null;
  badges: BadgeCatalogResponse | null;
  streaks: Record<string, StreakPayload>;
  /**
   * Path slice. The mount-time slot key is either the explicit
   * `data-path-key` (for caller-driven multi-mount pages) or `__active__`
   * for the audience's resolved active path. Null payload means "no
   * active path for this audience" — the widget renders empty.
   */
  paths: Record<string, PathPayload | null>;
  /** Token wallet (primary balance + progression tokens). Null pre-hydrate. */
  tokens: TokensPayload | null;
  /** Owner key for the token wallet payload, scoped to API base + widget token. */
  tokensContext: string | null;
  pendingOps: PendingOp[];
  etag: string | null;
  version: number;
  /** Scopes carried by the mounted widget token. Empty array means we
   *  don't know yet (pre-hydrate); used by widgets to pre-empt 403s. */
  scopes: string[];
  /** Resolved "Powered by Hatched" attribution. Null pre-hydrate; widgets
   *  treat the absence as "don't render the badge yet" (no flash of the
   *  default before the server-clamped mode arrives). */
  attribution: WidgetAttribution | null;
  /** Buddy-share feature hint from the server. Null pre-hydrate; `{ enabled }`
   *  once /widget/state arrives — the buddy widget shows its Share button only
   *  when `enabled === true`. */
  share: { enabled: boolean } | null;
  /** Per-feature configuration blob from the Planner drawer (HTCH-10+).
   *  Empty object pre-hydrate; populated by /widget/state. Widgets read
   *  their own slice (e.g. `feature_config.marketplace_dress`). */
  featureConfig: Record<string, unknown>;
}

const INITIAL_STATE: WorkspaceState = {
  buddy: null,
  badges: null,
  streaks: {},
  paths: {},
  tokens: null,
  tokensContext: null,
  pendingOps: [],
  etag: null,
  version: 0,
  scopes: [],
  attribution: null,
  share: null,
  featureConfig: {},
};

export class WorkspaceStore {
  private state: WorkspaceState = INITIAL_STATE;
  private listeners = new Set<() => void>();
  /**
   * Snapshot the pre-optimistic state per rollbackId. On reconcile we merge
   * server effects on top of the pre-optimistic snapshot so the optimistic
   * delta doesn't double-count.
   */
  private optimisticSnapshots = new Map<string, WorkspaceState>();

  getState(): Readonly<WorkspaceState> {
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Replace known slices from an aggregate snapshot — called after
   * /widget/state and on cross-tab BroadcastChannel rehydrates.
   */
  hydrate(patch: Partial<WorkspaceState>): void {
    this.state = {
      ...this.state,
      ...patch,
      version: this.state.version + 1,
    };
    this.emit();
  }

  /**
   * Merge server-computed effects onto the current state. Badge awards and
   * streak updates are union-merged; evolution readiness promotes to a
   * pendingOp so the evolution ceremony widget can spin its shimmer.
   */
  applyEffects(effects: EventEffects): void {
    const current = this.state;
    const nextBuddy: BuddyData | null = current.buddy
      ? { ...current.buddy, coins: current.buddy.coins + effects.coins }
      : null;

    let nextBadges = current.badges;
    if (nextBadges && effects.badges_awarded.length > 0) {
      const earnedKeys = new Set(nextBadges.earned.map((b) => b.badge_key));
      const lockedByKey = new Map(nextBadges.locked.map((b) => [b.key, b]));
      const newlyEarned: EarnedBadge[] = [];
      for (const key of effects.badges_awarded) {
        if (earnedKeys.has(key)) continue;
        const locked = lockedByKey.get(key);
        newlyEarned.push({
          badge_key: key,
          label: locked?.label ?? key,
          description: locked?.description ?? null,
          criteria_copy: locked?.criteria_copy ?? null,
          icon_url: locked?.icon_url ?? null,
          awarded_at: new Date().toISOString(),
          coin_reward: locked?.coin_reward ?? 0,
        });
        lockedByKey.delete(key);
      }
      if (newlyEarned.length > 0) {
        const earnedAtByKey = new Map(newlyEarned.map((badge) => [badge.badge_key, badge.awarded_at]));
        nextBadges = {
          ...nextBadges,
          earned: [...nextBadges.earned, ...newlyEarned],
          locked: Array.from(lockedByKey.values()),
          badges: nextBadges.badges?.map((badge) => {
            const earnedAt = badge.key ? earnedAtByKey.get(badge.key) : undefined;
            return earnedAt ? { ...badge, earned_at: earnedAt, progress: undefined } : badge;
          }),
        };
      }
    }

    const nextStreaks = { ...current.streaks };
    for (const update of effects.streak_updates ?? []) {
      const prev = nextStreaks[update.definition_key];
      if (!prev) continue;
      nextStreaks[update.definition_key] = {
        ...prev,
        progress: {
          ...prev.progress,
          current: update.current,
          longest: Math.max(prev.progress.longest, update.longest),
        },
        is_active_period: true,
      };
    }

    let nextPaths = current.paths;
    if (effects.path_updates && effects.path_updates.length > 0) {
      nextPaths = mergePathUpdates(current.paths, effects.path_updates);
    }

    let nextTokens = current.tokens;
    if (nextTokens && effects.tokens.length > 0) {
      nextTokens = mergeTokenEffects(nextTokens, effects.tokens);
    }

    this.state = {
      ...current,
      buddy: nextBuddy,
      badges: nextBadges,
      streaks: nextStreaks,
      paths: nextPaths,
      tokens: nextTokens,
      version: current.version + 1,
    };
    this.emit();
  }

  applyOptimistic(patch: Partial<WorkspaceState>, rollbackId: string): void {
    if (!this.optimisticSnapshots.has(rollbackId)) {
      this.optimisticSnapshots.set(rollbackId, this.state);
    }
    this.state = {
      ...this.state,
      ...patch,
      version: this.state.version + 1,
    };
    this.emit();
  }

  /**
   * Roll optimistic state back to its pre-patch snapshot. Used when a track()
   * POST fails — the UI reverts to what the server last confirmed.
   */
  rollback(rollbackId: string): void {
    const snap = this.optimisticSnapshots.get(rollbackId);
    if (!snap) return;
    this.optimisticSnapshots.delete(rollbackId);
    this.state = { ...snap, version: this.state.version + 1 };
    this.emit();
  }

  /**
   * Discard the snapshot without reverting — call this after the server
   * confirms the effect so we don't accumulate snapshots forever.
   */
  commitOptimistic(rollbackId: string): void {
    this.optimisticSnapshots.delete(rollbackId);
  }

  setStreak(key: string, data: StreakPayload): void {
    this.state = {
      ...this.state,
      streaks: { ...this.state.streaks, [key]: data },
      version: this.state.version + 1,
    };
    this.emit();
  }

  /** Replace a path slot's payload (initial hydrate or full refresh). */
  setPath(slot: string, data: PathPayload | null): void {
    this.state = {
      ...this.state,
      paths: { ...this.state.paths, [slot]: data },
      version: this.state.version + 1,
    };
    this.emit();
  }

  /** Replace the token wallet payload (initial hydrate or full refresh). */
  setTokens(data: TokensPayload | null, context: string | null = this.state.tokensContext): void {
    this.state = {
      ...this.state,
      tokens: data,
      tokensContext: context,
      version: this.state.version + 1,
    };
    this.emit();
  }

  /**
   * Optimistically mark a sub-step (and possibly its step / path) as
   * completed in the named slot. Used after a successful manual-complete
   * round-trip so the widget paints without waiting on the next poll.
   */
  applyPathLocalCompletion(
    slot: string,
    stepKey: string,
    subStepKey: string,
    flags: { stepCompleted: boolean; pathCompleted: boolean },
  ): void {
    const existing = this.state.paths[slot];
    if (!existing) return;
    const updated = applyPathCompletion(existing, stepKey, subStepKey, flags);
    this.state = {
      ...this.state,
      paths: { ...this.state.paths, [slot]: updated },
      version: this.state.version + 1,
    };
    this.emit();
  }

  markPending(op: PendingOp): void {
    const existing = this.state.pendingOps.find((p) => p.id === op.id);
    if (existing) return;
    this.state = {
      ...this.state,
      pendingOps: [...this.state.pendingOps, op],
      version: this.state.version + 1,
    };
    this.emit();
  }

  resolvePending(
    opId: string,
    result?: Record<string, unknown>,
  ): Record<string, unknown> | null {
    const remaining = this.state.pendingOps.filter((p) => p.id !== opId);
    if (remaining.length === this.state.pendingOps.length) return null;
    this.state = {
      ...this.state,
      pendingOps: remaining,
      version: this.state.version + 1,
    };
    this.emit();
    return result ?? null;
  }

  setEtag(etag: string | null): void {
    if (this.state.etag === etag) return;
    this.state = { ...this.state, etag };
    // Intentionally no emit: etag changes don't affect rendered state.
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

function applyPathCompletion(
  payload: PathPayload,
  stepKey: string,
  subStepKey: string,
  flags: { stepCompleted: boolean; pathCompleted: boolean },
): PathPayload {
  const nowIso = new Date().toISOString();
  const nextSteps = payload.steps.map((step) => {
    if (step.key !== stepKey) return step;
    const nextSubs = step.sub_steps.map((sub) =>
      sub.key === subStepKey
        ? ({ ...sub, status: 'completed', completed_at: nowIso } as PathSubStepView)
        : sub,
    );
    // Re-derive intra-step "available" slots: a previously-locked sub-step
    // that's now unblocked moves to available.
    const reflowed = reflowSubSteps(nextSubs);
    return {
      ...step,
      sub_steps: reflowed,
      completed: flags.stepCompleted || step.completed,
    };
  });
  // Important: do NOT optimistically unlock the next step here. The next
  // step's `unlocked` depends on its `unlock_condition` — a predicate
  // evaluated server-side against buddy state we don't have on the
  // client. Leave downstream steps as the server last reported them; the
  // next /widget/state poll (or a fresh /widget/path fetch) reconciles
  // the gate truth.
  return {
    ...payload,
    steps: nextSteps,
    completed: flags.pathCompleted || payload.completed,
    completed_at: flags.pathCompleted ? nowIso : payload.completed_at,
    current_step_key: flags.pathCompleted
      ? null
      : computeCurrentStepKey(nextSteps),
  };
}

function reflowSubSteps(subs: PathSubStepView[]): PathSubStepView[] {
  return subs.map((sub) => {
    if (sub.status === 'completed') return sub;
    if (sub.allow_skip_ahead) {
      return { ...sub, status: 'available' as const };
    }
    const priorComplete = subs
      .filter((s) => s.ordinal < sub.ordinal)
      .every((s) => s.status === 'completed');
    return { ...sub, status: priorComplete ? 'available' : 'locked' };
  });
}

function computeCurrentStepKey(steps: PathStepView[]): string | null {
  for (const step of steps) {
    if (step.unlocked && !step.completed) return step.key;
  }
  return null;
}

function mergeTokenEffects(payload: TokensPayload, effects: string[]): TokensPayload {
  const deltas = new Map<string, number>();
  for (const effect of effects) {
    const [key, rawAmount] = effect.split(':');
    const amount = Number(rawAmount);
    if (!key || !Number.isFinite(amount)) continue;
    deltas.set(key, (deltas.get(key) ?? 0) + amount);
  }
  if (deltas.size === 0) return payload;

  let changed = false;
  const progression = payload.progression.map((token) => {
    const delta = deltas.get(token.token_key);
    if (!delta) return token;
    changed = true;
    return {
      ...token,
      balance: Math.max(0, Math.trunc(token.balance + delta)),
    };
  });

  return changed ? { ...payload, progression } : payload;
}

function mergePathUpdates(
  paths: Record<string, PathPayload | null>,
  updates: PathUpdate[],
): Record<string, PathPayload | null> {
  // Updates target a definition key. Find every slot whose payload uses
  // that key (the explicit-key mount and the __active__ default both
  // resolve to the same definition, so both apply).
  const next = { ...paths };
  for (const update of updates) {
    if (!update.sub_step_completed) continue;
    for (const slot of Object.keys(next)) {
      const payload = next[slot];
      if (!payload || payload.definition.key !== update.path_key) continue;
      next[slot] = applyPathCompletion(
        payload,
        update.sub_step_completed.step_key,
        update.sub_step_completed.sub_step_key,
        {
          stepCompleted: Boolean(update.step_completed),
          pathCompleted: Boolean(update.path_completed),
        },
      );
    }
  }
  return next;
}

type HatchedGlobal = {
  __HATCHED_WIDGET__?: {
    store?: WorkspaceStore;
  };
};

/**
 * Returns the shared store instance. Loader creates the real one; if a widget
 * mounts before the loader has run (shouldn't happen in practice, but guards
 * against bundler load-order surprises), we fall back to a local store so the
 * widget renders its initial empty state instead of crashing.
 */
export function getStore(): WorkspaceStore {
  const globalScope = (typeof window !== 'undefined'
    ? (window as unknown as HatchedGlobal)
    : ({} as HatchedGlobal));
  if (globalScope.__HATCHED_WIDGET__?.store) {
    return globalScope.__HATCHED_WIDGET__.store;
  }
  // Lazily attach a fallback so subsequent calls from the same bundle see
  // the same instance. The loader will overwrite this if it runs later.
  const fallback = new WorkspaceStore();
  if (typeof window !== 'undefined') {
    const w = window as unknown as HatchedGlobal;
    w.__HATCHED_WIDGET__ = w.__HATCHED_WIDGET__ ?? {};
    if (!w.__HATCHED_WIDGET__.store) w.__HATCHED_WIDGET__.store = fallback;
  }
  return fallback;
}
